import React, { useMemo, useState } from 'react';
import { Image } from 'lucide-react';
import { useStorageUrl } from '../../hooks/useStorageUrl';

interface ImageWithFallbackProps {
  path: string | string[] | null | undefined;
  alt: string;
  className?: string;
}

export default function ImageWithFallback({ path, alt, className }: ImageWithFallbackProps) {
  const paths = useMemo(
    () => (Array.isArray(path) ? path : [path])
      .map(value => value?.trim())
      .filter((value): value is string => Boolean(value)),
    [path]
  );
  const pathKey = paths.join('|');
  const [pathIndex, setPathIndex] = useState(0);
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const { url, loading } = useStorageUrl(paths[pathIndex]);
  const isImageLoaded = Boolean(url && loadedUrl === url);
  const isImageFailed = Boolean(url && failedUrl === url);
  const showLoadingBlock = loading || Boolean(url && !isImageLoaded && !isImageFailed);

  React.useEffect(() => {
    setPathIndex(0);
    setLoadedUrl(null);
    setFailedUrl(null);
  }, [pathKey]);

  React.useEffect(() => {
    if (!loading && !url && pathIndex < paths.length - 1) {
      setPathIndex(current => current + 1);
    }
  }, [loading, pathIndex, paths.length, url]);

  return (
    <div className={`relative overflow-hidden bg-gray-200 ${className}`}>
      {showLoadingBlock && (
        <div
          className="absolute inset-0 bg-gradient-to-br from-gray-200 via-gray-100 to-gray-200 animate-[pulse_2.8s_ease-in-out_infinite] motion-reduce:animate-none"
          aria-hidden="true"
        />
      )}

      {url && !isImageFailed && (
        <img
          src={url}
          alt={alt}
          loading="lazy"
          decoding="async"
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${
            isImageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setLoadedUrl(url)}
          onError={() => {
            setFailedUrl(url);
            if (pathIndex < paths.length - 1) {
              setPathIndex(current => current + 1);
            }
          }}
        />
      )}

      {!loading && (!url || isImageFailed) && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
          <Image className="w-6 h-6 text-gray-400" aria-hidden="true" />
        </div>
      )}
    </div>
  );
}
